import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

interface Department {
  id: string | number;
  name: string;
}

interface Option {
  value: string;
  label: string;
}

interface FilterState {
  departments: (string | number)[];
  likes: string[];
  abilities: string[];
}

interface CareerFilterProps {
  onFilterChange: (filters: FilterState) => void;
  resetKey?: number;
}

const CareerFilter: React.FC<CareerFilterProps> = ({ onFilterChange, resetKey = 0 }) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<(string | number)[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [likeSearchQuery, setLikeSearchQuery] = useState<string>("");
  const [selectedLikes, setSelectedLikes] = useState<string[]>([]);
  const [abilitySearchQuery, setAbilitySearchQuery] = useState<string>("");
  const [selectedAbilities, setSelectedAbilities] = useState<string[]>([]);

  const location = useLocation();

  const likesOptions: Option[] = [
    { value: "Being Creative and Innovative", label: "Being Creative and Innovative" },
    { value: "Communicating with People", label: "Communicating with People" },
    { value: "Designing and Styling", label: "Designing and Styling" },
    { value: "Helping and Guiding People", label: "Helping and Guiding People" },
    { value: "Learning about other Cultures", label: "Learning about other Cultures" },
    { value: "Media, Glamour and Entertainment", label: "Media, Glamour and Entertainment" },
    { value: "Nature and Outdoors", label: "Nature and Outdoors" },
    { value: "Observing Human Behaviour", label: "Observing Human Behaviour" },
    { value: "Organizing and Structuring", label: "Organizing and Structuring" },
    { value: "Physical Activities and Sports", label: "Physical Activities and Sports" },
    { value: "Playing with Numbers", label: "Playing with Numbers" },
    { value: "Solving Problems", label: "Solving Problems" },
    { value: "Taking Risk and Adventure", label: "Taking Risk and Adventure" },
    { value: "Technology and Gadgets", label: "Technology and Gadgets" },
  ];

  const naturalAbilitiesOptions: Option[] = [
    { value: "Analytical & Critical thinking", label: "Analytical & Critical thinking" },
    { value: "Computer Skills", label: "Computer Skills" },
    { value: "Dealing with People", label: "Dealing with People" },
    { value: "Drawing and Sketching", label: "Drawing and Sketching" },
    { value: "Imagining Things", label: "Imagining Things" },
    { value: "Making Decisions", label: "Making Decisions" },
    { value: "Planning and Supervising", label: "Planning and Supervising" },
    { value: "Scientific Curiosity", label: "Scientific Curiosity" },
    { value: "Solving Quantitative Problems", label: "Solving Quantitative Problems" },
    { value: "Team Work", label: "Team Work" },
    { value: "Verbal Communication", label: "Verbal Communication" },
    { value: "Working with Machines and Tools", label: "Working with Machines and Tools" },
    { value: "Working with Numerical Data", label: "Working with Numerical Data" },
  ];

  // Fetch departments
  useEffect(() => {
    axios
      .get<{ success: boolean; data: Department[][] }>(
        `${process.env.REACT_APP_SFE_API}career/departments`
      )
      .then((response) => {
        if (response.data.success) {
          setDepartments(response.data.data[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching department data:", error);
      });
  }, []);

  // Parse URL params to pre-select filters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const likeFromUrl = params.get("like");
    const abilityFromUrl = params.get("ability");

    if (likeFromUrl) setSelectedLikes([likeFromUrl]);
    if (abilityFromUrl) setSelectedAbilities([abilityFromUrl]);
  }, [location.search]);

  // Handle filter reset
  useEffect(() => {
    if (resetKey > 0) {
      setSelectedDepartments([]);
      setSelectedLikes([]);
      setSelectedAbilities([]);
      setSearchQuery("");
      setLikeSearchQuery("");
      setAbilitySearchQuery("");
    }
  }, [resetKey]);

  // Notify parent about filter changes
  useEffect(() => {
    onFilterChange({
      departments: selectedDepartments,
      likes: selectedLikes,
      abilities: selectedAbilities,
    });
  }, [selectedDepartments, selectedLikes, selectedAbilities, onFilterChange]);

  const handleCheckboxChange = <T extends string | number>(
    id: T,
    selectedItems: T[],
    setSelectedItems: React.Dispatch<React.SetStateAction<T[]>>
  ) => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  const filteredDepartments = departments.filter((department) =>
    department?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLikes = likesOptions.filter((like) =>
    like?.label?.toLowerCase().includes(likeSearchQuery.toLowerCase())
  );

  const filteredAbilities = naturalAbilitiesOptions.filter((ability) =>
    ability?.label?.toLowerCase().includes(abilitySearchQuery.toLowerCase())
  );

  // Use React state to control accordion open/close so clicks toggle correctly
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const handleAccordionClick = (index: number) => {
    setOpenAccordion((prev) => (prev === index ? null : index));
  };

  return (
    <div className="filterGroup" id="accordionFilters">
      {/* Department Filter */}
      <div className="fiterItem mb-3 bg-white">
        <div className="filterHeader">
          <div className="d-flex justify-content-between align-items-center position-relative">
            <button
              className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
              type="button"
              aria-expanded={openAccordion === 0}
              onClick={() => handleAccordionClick(0)}
            >
              <span className="font-18 font-600">Department</span>
              <span className="toggle"></span>
            </button>
          </div>
        </div>
        <div className={`collapse ${openAccordion === 0 ? "show" : ""}`} id="collapseShortlistedCourse">
          <div className="filterSearchblk mb-3">
            <form
              className="d-flex align-items-center filterSearch position-relative"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                id="search_dept"
                name="search_dept"
                className="form-control flex-1 font-14 font-500 w-100"
                type="search"
                placeholder="Search Departments"
                aria-label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          </div>
          <div className="card card-body mt-2">
            <div className="overflow-hidden">
              <div className="scrollMe">
                {filteredDepartments.length > 0 ? (
                  filteredDepartments.map(({ id, name }) => (
                    <div className="form-check d-flex flex-wrap" key={id}>
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        value={id}
                        id={`department-${id}`}
                        onChange={() =>
                          handleCheckboxChange(id, selectedDepartments, setSelectedDepartments)
                        }
                        checked={selectedDepartments.includes(id)}
                      />
                      <label
                        className="form-check-label font-16 font-400 flex-1 ps-1 pt-1"
                        htmlFor={`department-${id}`}
                      >
                        {name}
                      </label>
                    </div>
                  ))
                ) : (
                  <p>No departments found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Likes Filter */}
      <div className="fiterItem mb-3 bg-white">
        <div className="filterHeader">
          <div className="d-flex justify-content-between align-items-center position-relative">
            <button
              className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
              type="button"
              aria-expanded={openAccordion === 1}
              onClick={() => handleAccordionClick(1)}
            >
              <span className="font-18 font-600">What all do you like?</span>
              <span className="toggle"></span>
            </button>
          </div>
        </div>
        <div className={`collapse ${openAccordion === 1 ? "show" : ""}`} id="collapseLikes">
          <div className="filterSearchblk mb-3">
            <form
              className="d-flex align-items-center filterSearch position-relative"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                className="form-control flex-1 font-14 font-500 w-100"
                type="search"
                placeholder="Search"
                aria-label="Search"
                value={likeSearchQuery}
                onChange={(e) => setLikeSearchQuery(e.target.value)}
              />
            </form>
          </div>
          <div className="card card-body">
            <div className="overflow-hidden">
              <div className="scrollMe">
                {filteredLikes.map(({ value, label }) => (
                  <div className="form-check d-flex flex-wrap" key={value}>
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id={`like-${value}`}
                      onChange={() =>
                        handleCheckboxChange(value, selectedLikes, setSelectedLikes)
                      }
                      checked={selectedLikes.includes(value)}
                    />
                    <label
                      className="form-check-label font-16 font-400 flex-1 ps-1 pt-1"
                      htmlFor={`like-${value}`}
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Abilities Filter */}
      <div className="fiterItem mb-3 bg-white">
        <div className="filterHeader">
          <div className="d-flex justify-content-between align-items-center position-relative">
            <button
              className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
              type="button"
              aria-expanded={openAccordion === 2}
              onClick={() => handleAccordionClick(2)}
            >
              <span className="font-18 font-600">What you are naturally good at?</span>
              <span className="toggle"></span>
            </button>
          </div>
        </div>
        <div className={`collapse ${openAccordion === 2 ? "show" : ""}`} id="collapseAbilities">
          <div className="filterSearchblk mb-3">
            <form
              className="d-flex align-items-center filterSearch position-relative"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                className="form-control flex-1 font-14 font-500 w-100"
                type="search"
                placeholder="Search"
                aria-label="Search"
                value={abilitySearchQuery}
                onChange={(e) => setAbilitySearchQuery(e.target.value)}
              />
            </form>
          </div>
          <div className="card card-body">
            <div className="overflow-hidden">
              <div className="scrollMe">
                {filteredAbilities.map(({ value, label }) => (
                  <div className="form-check d-flex flex-wrap" key={value}>
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id={`ability-${value}`}
                      onChange={() =>
                        handleCheckboxChange(value, selectedAbilities, setSelectedAbilities)
                      }
                      checked={selectedAbilities.includes(value)}
                    />
                    <label
                      className="form-check-label font-16 font-400 flex-1 ps-1 pt-1"
                      htmlFor={`ability-${value}`}
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerFilter;